import React, { useCallback } from 'react';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { HOLIDAY_OPTIONS, HolidayValue, PRIMARY_HOLIDAYS, SECONDARY_HOLIDAYS } from '@/constants/holidays';

export interface HolidaySelectorProps {
  /** Currently selected holidays */
  value: string[];
  /** Callback when selection changes */
  onChange: (holidays: string[]) => void;
  /** UI mode: checkbox grid or toggle badges */
  mode?: 'checkbox' | 'badge';
  /** Layout direction */
  layout?: 'grid' | 'row';
  /** Show emoji icons */
  showIcons?: boolean;
  /** Maximum number of selections allowed */
  maxSelections?: number;
  /** Disabled state */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Show section labels (Primary/Other) */
  showSections?: boolean;
}

/**
 * HolidaySelector - Standardized holiday multi-select component
 *
 * Features:
 * - Type-safe holiday selection
 * - Accessible (ARIA labels, keyboard navigation)
 * - Flexible UI modes (checkboxes or badges)
 * - Responsive grid layout
 * - Optional icons and section labels
 *
 * @example
 * ```tsx
 * <HolidaySelector
 *   value={holidays}
 *   onChange={setHolidays}
 *   mode="checkbox"
 *   showIcons={true}
 * />
 * ```
 */
export const HolidaySelector: React.FC<HolidaySelectorProps> = ({
  value,
  onChange,
  mode = 'checkbox',
  layout = 'grid',
  showIcons = true,
  maxSelections,
  disabled = false,
  className,
  showSections = false,
}) => {
  const isSelected = useCallback(
    (holidayValue: HolidayValue) => value.includes(holidayValue),
    [value]
  );

  const isMaxReached = useCallback(
    () => maxSelections !== undefined && value.length >= maxSelections,
    [maxSelections, value.length]
  );

  const handleToggle = useCallback(
    (holidayValue: HolidayValue) => {
      if (disabled) return;

      const isCurrentlySelected = isSelected(holidayValue);

      if (isCurrentlySelected) {
        // Remove from selection
        onChange(value.filter(v => v !== holidayValue));
      } else {
        // Add to selection (if under max limit)
        if (!isMaxReached()) {
          onChange([...value, holidayValue]);
        }
      }
    },
    [disabled, value, onChange, isSelected, isMaxReached]
  );

  const renderCheckboxMode = () => {
    const gridClass = layout === 'grid'
      ? 'grid grid-cols-2 md:grid-cols-3 gap-3'
      : 'flex flex-wrap gap-3';

    const renderHolidayCheckbox = (holiday: typeof HOLIDAY_OPTIONS[number]) => {
      const selected = isSelected(holiday.value);
      const canSelect = !isMaxReached() || selected;

      return (
        <div
          key={holiday.value}
          className={cn(
            'flex items-center space-x-2',
            !canSelect && 'opacity-50 cursor-not-allowed'
          )}
        >
          <Checkbox
            id={`holiday-${holiday.value}`}
            checked={selected}
            onCheckedChange={() => handleToggle(holiday.value)}
            disabled={disabled || (!canSelect && !selected)}
            aria-label={`Select ${holiday.label}`}
          />
          <Label
            htmlFor={`holiday-${holiday.value}`}
            className={cn(
              'text-sm font-normal cursor-pointer',
              disabled && 'cursor-not-allowed'
            )}
          >
            {showIcons && <span className="mr-1">{holiday.icon}</span>}
            {holiday.label}
          </Label>
        </div>
      );
    };

    if (showSections) {
      return (
        <div className="space-y-4">
          {PRIMARY_HOLIDAYS.length > 0 && (
            <div>
              <h4 className="text-sm font-medium mb-2 text-muted-foreground">Popular Holidays</h4>
              <div className={gridClass}>
                {PRIMARY_HOLIDAYS.map(renderHolidayCheckbox)}
              </div>
            </div>
          )}
          {SECONDARY_HOLIDAYS.length > 0 && (
            <div>
              <h4 className="text-sm font-medium mb-2 text-muted-foreground">Other Holidays</h4>
              <div className={gridClass}>
                {SECONDARY_HOLIDAYS.map(renderHolidayCheckbox)}
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className={gridClass}>
        {HOLIDAY_OPTIONS.map(renderHolidayCheckbox)}
      </div>
    );
  };

  const renderBadgeMode = () => {
    const wrapperClass = layout === 'grid'
      ? 'grid grid-cols-2 md:grid-cols-3 gap-2'
      : 'flex flex-wrap gap-2';

    return (
      <div className={wrapperClass}>
        {HOLIDAY_OPTIONS.map(holiday => {
          const selected = isSelected(holiday.value);
          const canSelect = !isMaxReached() || selected;

          return (
            <Badge
              key={holiday.value}
              variant={selected ? 'default' : 'outline'}
              className={cn(
                'cursor-pointer transition-all px-3 py-1.5 text-sm',
                'hover:shadow-sm hover:scale-105',
                selected && 'bg-primary text-primary-foreground',
                !canSelect && 'opacity-50 cursor-not-allowed hover:scale-100',
                disabled && 'cursor-not-allowed opacity-50 hover:scale-100'
              )}
              onClick={() => !disabled && canSelect && handleToggle(holiday.value)}
              role="checkbox"
              aria-checked={selected}
              aria-label={`Toggle ${holiday.label}`}
              tabIndex={disabled ? -1 : 0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleToggle(holiday.value);
                }
              }}
            >
              {showIcons && <span className="mr-1">{holiday.icon}</span>}
              {holiday.label}
            </Badge>
          );
        })}
      </div>
    );
  };

  return (
    <div className={cn('space-y-2', className)}>
      {mode === 'checkbox' ? renderCheckboxMode() : renderBadgeMode()}
      {maxSelections && (
        <p className="text-xs text-muted-foreground">
          {value.length} / {maxSelections} selected
        </p>
      )}
    </div>
  );
};

/**
 * Memoized version for performance optimization
 */
export const MemoizedHolidaySelector = React.memo(HolidaySelector);

export default HolidaySelector;
